import { Box, Grid, Stack, Typography } from '@mui/material';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import { FILTER_DATE_MIN_WIDTH } from 'app/components/elements/DatePicker';
import { ReactNode } from 'react';
import { IssuesExportMenu } from './IssuesExportMenu';

export const FILTER_CONTROL_HEIGHT = 46;

export const filterFieldSx = {
  padding: 0,
  minHeight: `${FILTER_CONTROL_HEIGHT}px`,
  height: `${FILTER_CONTROL_HEIGHT}px`,
  width: '100%'
};

type IssuesFiltersBarProps = {
  children: ReactNode;
  onExportCsv?: () => void | Promise<void>;
  onExportXlsx?: () => void | Promise<void>;
  exporting?: boolean;
  onClearFilters?: () => void;
  showClearFilters?: boolean;
  helperText?: string;
};

/** Full-width panel for search, filters, and export actions on Issues tabs. */
export const IssuesFiltersBar = ({
  children,
  onExportCsv,
  onExportXlsx,
  exporting,
  onClearFilters,
  showClearFilters,
  helperText
}: IssuesFiltersBarProps) => {
  const hasExport = !!onExportCsv || !!onExportXlsx;

  return (
    <Box
      sx={{
        width: '100%',
        border: '1px solid #e5e7eb',
        borderRadius: 2,
        bgcolor: '#f8fafc',
        p: { xs: 1.5, sm: 2 }
      }}>
      {helperText ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {helperText}
        </Typography>
      ) : null}

      <Grid container spacing={2} alignItems="flex-end">
        {children}

        {(showClearFilters || hasExport) && (
          <Grid
            item
            xs={12}
            lg="auto"
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'flex-end',
              gap: 1.25,
              width: { xs: '100%', lg: 'auto' },
              ml: { lg: 'auto' },
              flexShrink: 0
            }}>
            {showClearFilters && onClearFilters ? (
              <ButtonCustom
                isSecondary
                sx={{
                  width: { xs: '100%', sm: 'max-content' },
                  height: `${FILTER_CONTROL_HEIGHT}px`,
                  bgcolor: '#fff',
                  border: '1px solid #ccc',
                  '&:hover': {
                    bgcolor: '#f1f1f1'
                  }
                }}
                label="Clear filters"
                onClick={onClearFilters}
              />
            ) : null}
            {hasExport ? (
              <IssuesExportMenu
                disabled={exporting}
                loading={exporting}
                onExportCsv={onExportCsv}
                onExportXlsx={onExportXlsx}
              />
            ) : null}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

/** Search field + optional apply button in one grid cell. */
export const IssuesSearchField = ({
  children,
  onSearch,
  showSearchButton = true
}: {
  children: ReactNode;
  onSearch?: () => void;
  showSearchButton?: boolean;
}) => (
  <Grid item xs={12} md={6} lg={5} xl={4}>
    <Stack direction="row" gap={1.25} alignItems="flex-start" width="100%">
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      {showSearchButton && onSearch ? (
        <Stack flexShrink={0} alignItems="stretch">
          {/* Match InputText / InputSelect FormLabel height so the button lines up with inputs */}
          <Typography
            component="span"
            aria-hidden
            sx={{
              mb: 1.25,
              lineHeight: 1.5,
              fontSize: '1rem',
              visibility: 'hidden',
              userSelect: 'none'
            }}>
            &nbsp;
          </Typography>
          <ButtonCustom
            sx={{
              width: 'max-content',
              height: `${FILTER_CONTROL_HEIGHT}px`,
              minHeight: `${FILTER_CONTROL_HEIGHT}px`,
              flexShrink: 0
            }}
            label="Search"
            onClick={onSearch}
          />
        </Stack>
      ) : null}
    </Stack>
  </Grid>
);

/** Standard width for a filter dropdown column. */
export const IssuesFilterField = ({
  children,
  xs = 12,
  sm = 6,
  md = 4,
  lg = 3,
  xl = 3
}: {
  children: ReactNode;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}) => (
  <Grid item xs={xs} sm={sm} md={md} lg={lg} xl={xl} sx={{ minWidth: 0 }}>
    {children}
  </Grid>
);

/** Date filters need a wider column so the picker placeholder and label stay readable. */
export const IssuesDateFilterField = ({
  children,
  xs = 12,
  sm = 6,
  md = 4,
  lg = 3,
  xl = 3
}: {
  children: ReactNode;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}) => (
  <Grid
    item
    xs={xs}
    sm={sm}
    md={md}
    lg={lg}
    xl={xl}
    sx={{ minWidth: { xs: '100%', sm: FILTER_DATE_MIN_WIDTH } }}>
    {children}
  </Grid>
);
